import {
  Box,
  Button,
  ColumnLayout,
  FormField,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import React, { useEffect, useState } from 'react';

import { getTaggedResources, listDataPermissions } from 'src/api/permissions';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { isValidPrincipalNotRoot, isValidWorkspace } from 'src/commons/validationUtils';
import {
  ALL_TABLES_RES_NAME,
  DATA_PERMISSION_CONSUMER_OPTION,
  DATA_PERMISSION_LAKE_FORMATION_TYPE,
  DATA_PERMISSION_PUBLISHER_OPTION,
  DATA_PERMISSION_STATUS_ACTIVE,
  DATABASE_RESOURCE_TYPE,
  DATASET_RESOURCE_TYPE,
  GALAXI_TYPE,
  LAKE_FORMATION_DATASOURCE_ID,
  LAKE_FORMATION_TAG_ITEM,
  LAKEFORMATION_RESOURCE,
  LAKEFORMATION_TAG_TYPE,
  RESOURCE_ITEM,
  S3_TABLE_DATASOURCE_ID,
  TABLE_RESOURCE_TYPE,
  TAG_ITEM,
} from 'src/commons/constants';
import { createDatabaseDetailLink, createDatasetDetailLink } from 'src/routes';
import { getConsumerDataLakePrincipal, getFoundryRoleARN } from 'src/commons/common';
import { isMPData } from 'src/components/workspaces/common/common';
import { EmptyState } from 'src/commons/EmptyState';
import { isDWaaSGroup } from 'src/api/config';
import { GalaxiGroup } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { CartAccessItem } from 'src/components/common/checkout/common';

export interface AccessSelectModalProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  visible: boolean;
  dismiss: any;
  database: any;
  addToCart: (items: CartAccessItem[]) => void;
  catalogName: any;
  tags: any[];
  cartItemIds: any;
  cartItems: any;
  galaxiGroups?: GalaxiGroup[];
}

export const AccessSelectModal = (props: AccessSelectModalProps) => {
  const [selectedPermission, setSelectedPermission] = useState(undefined);
  const [loadingResources, setLoadingResources] = useState(false);
  const [allItems, setItems] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [selectedPrincipalType, setSelectedPrincipalType] = useState(undefined);
  const [principal, setPrincipal] = useState(undefined);
  const [principalErrorText, setPrincipalErrorText] = useState(undefined);
  const [dropdownPrincipalOptions, setDropDownPrincipalOptions] = useState([]);
  const [galaxiGroupErrorText, setGalaxiGroupErrorText] = useState(undefined);
  const [selectedGalaxiGroup, setSelectedGalaxiGroup] = useState(undefined);

  const flattenDatasetAllTableAccess = (
    catalogId,
    catalogName,
    permissionId,
    owner,
    datasetName,
    databaseName,
    id,
    publisherRole,
    dataLakePrincipal,
  ): CartAccessItem => {
    return {
      datasetName: ALL_TABLES_RES_NAME,
      databaseName: databaseName,
      tableName: datasetName,
      catalogId: catalogId,
      region: props.database['Region'],
      catalogDisplayName: catalogName !== undefined ? catalogName : catalogId,
      dataOwnerRole: isValidWorkspace(owner) ? getFoundryRoleARN(props.database['Region'], catalogId) : publisherRole,
      owner: owner,
      itemType: RESOURCE_ITEM,
      permissionId: permissionId,
      catalogName: catalogName !== undefined ? catalogName : catalogId,
      id: id,
      dataLakePrincipal: getConsumerDataLakePrincipal(
        LAKEFORMATION_RESOURCE,
        dataLakePrincipal,
        props.activeWorkspace.accountId,
      ),
    };
  };

  let flattenTagAccess = async (
    catalogId,
    catalogName,
    permissionId,
    tagKey,
    tagValue,
    owner,
    id,
    dataLakePrincipal,
  ): Promise<CartAccessItem> => {
    let resources = [];
    let getTaggedResourcesResponse = await getTaggedResources({
      tagPair: tagKey + ':' + tagValue,
      catalogId: catalogId,
      region: props.database['Region'],
      type: LAKEFORMATION_TAG_TYPE,
    });
    for (let resource of getTaggedResourcesResponse.resources) {
      if (resource['ResourceType'] == DATASET_RESOURCE_TYPE) {
        resources.push(resource['CatalogId'] + '/' + resource['DatabaseName'] + '/' + resource['ResourceName']);
      } else if (resource['ResourceType'] == DATABASE_RESOURCE_TYPE) {
        resources.push(resource['CatalogId'] + '/' + resource['DatabaseName']);
      }
    }
    return {
      tagKey: tagKey,
      tagValue: tagValue,
      tag: tagKey + '.' + tagValue,
      owner: owner,
      permissionId: permissionId,
      itemType: LAKE_FORMATION_TAG_ITEM,
      resources: resources,
      catalogDisplayName: catalogName !== undefined ? catalogName : catalogId,
      dataOwnerRole: getFoundryRoleARN(props.database?.Region, catalogId),
      catalogId: catalogId,
      catalogName: catalogName !== undefined ? catalogName : catalogId,
      databaseName: undefined,
      datasetName: undefined,
      tableName: undefined,
      id: id,
      region: props.database?.Region,
      dataLakePrincipal: getConsumerDataLakePrincipal(
        LAKEFORMATION_RESOURCE,
        dataLakePrincipal,
        props.activeWorkspace.accountId,
      ),
    };
  };

  const verifyRegisteredTag = async (tagKey, tagValue, tagAccountId, tagWorkspaceId, tagId) => {
    let listDatabaseTagPermissionPublisherRequest = {
      ownerId: tagWorkspaceId,
      region: props.database['Region'],
      resource: {
        lFTagPolicy: {
          resourceType: TABLE_RESOURCE_TYPE,
          expression: [
            {
              tagKey: tagKey,
              tagValues: [tagValue],
            },
          ],
          catalogId: tagAccountId,
        },
      },
      option: DATA_PERMISSION_PUBLISHER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      nextToken: null,
    };
    let listDatabaseTagPermissionConsumerRequest = {
      ownerId: props.activeWorkspace.workspaceId,
      region: props.database['Region'],
      resource: {
        lFTagPolicy: {
          resourceType: TABLE_RESOURCE_TYPE,
          expression: [
            {
              tagKey: tagKey,
              tagValues: [tagValue],
            },
          ],
          catalogId: tagAccountId,
        },
      },
      option: DATA_PERMISSION_CONSUMER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      nextToken: null,
    };

    let accountConsumed = undefined;
    let dataLakePrincipals = [];
    let dataLakeIAMPrincipals = [];
    let permissionId = undefined;

    await listDataPermissions({
      ownerId: tagWorkspaceId,
      option: DATA_PERMISSION_PUBLISHER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      nextToken: null,
    });

    try {
      let listTagPermissionPublisherResponse = await listDataPermissions(listDatabaseTagPermissionPublisherRequest);
      let listTagPermissionConsumersResponse = await listDataPermissions(listDatabaseTagPermissionConsumerRequest);
      if (
        listTagPermissionPublisherResponse?.dataPermissionList !== undefined &&
        listTagPermissionPublisherResponse?.dataPermissionList.length != 0
      ) {
        permissionId = listTagPermissionPublisherResponse.dataPermissionList[0].dataPermissionId;
      }
      if (
        listTagPermissionConsumersResponse?.dataPermissionList !== undefined &&
        listTagPermissionConsumersResponse?.dataPermissionList.length != 0
      ) {
        //Get list of datalake Principals from data permissions
        dataLakePrincipals = listTagPermissionConsumersResponse.dataPermissionList.map(
          (dataPermission) => dataPermission.dataLakePrincipal,
        );
      }
      //Add in principals from cart
      dataLakePrincipals = dataLakePrincipals.concat(
        props.cartItems
          .filter((dataPermission) => dataPermission.id === tagId)
          .map((dataPermission) => dataPermission.dataLakePrincipal),
      );
      dataLakeIAMPrincipals = dataLakePrincipals.filter(isValidPrincipalNotRoot);
      //Current workspace already consuming the all tables in database
      accountConsumed =
        dataLakePrincipals.filter((datalakePrincipal) => props.activeWorkspace.workspaceId.includes(datalakePrincipal))
          .length !== 0;
    } catch (err) {
      console.log('Exception list tag permission', err);
      return permissionId;
    }
    return {
      permissionId: permissionId,
      accountConsumed: accountConsumed,
      iamPrincipals: dataLakeIAMPrincipals,
    };
  };

  const verifyAllTableAccess = async (databaseName, catalogId, id) => {
    // check if there is a publisher with All Table permission
    let listDatasetPermissionRequestPublisherWithAllTables = {
      region: props.database['Region'],
      resource: {
        table: {
          databaseName: databaseName,
          name: null,
          catalogId: catalogId,
          tableWildcard: {},
        },
      },
      option: DATA_PERMISSION_PUBLISHER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      nextToken: null,
    };
    // check if the current workspace is already consuming the data
    let listDatasetPermissionRequestConsumerWithAllTables = {
      ownerId: props.activeWorkspace.workspaceId, // workspace owner
      region: props.database['Region'],
      resource: {
        table: {
          databaseName: databaseName,
          name: null,
          catalogId: catalogId,
          tableWildcard: {},
        },
      },
      option: DATA_PERMISSION_CONSUMER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      nextToken: null,
    };
    let accountConsumed = undefined;
    let permissionId = undefined;
    let publisherRole = undefined;
    let dataLakePrincipals = [];
    let dataLakeIAMPrincipals = [];
    try {
      let listDatasetPermissionResponsePublisher = await listDataPermissions(
        listDatasetPermissionRequestPublisherWithAllTables,
      );
      let listDatasetPermissionResponseConsumer = await listDataPermissions(
        listDatasetPermissionRequestConsumerWithAllTables,
      );
      if (
        listDatasetPermissionResponsePublisher?.dataPermissionList !== undefined &&
        listDatasetPermissionResponsePublisher.dataPermissionList.length != 0
      ) {
        permissionId = listDatasetPermissionResponsePublisher.dataPermissionList[0].dataPermissionId;
        publisherRole = listDatasetPermissionResponsePublisher.dataPermissionList[0].audit.PublisherRole;
      }
      if (
        listDatasetPermissionResponseConsumer?.dataPermissionList !== undefined &&
        listDatasetPermissionResponseConsumer.dataPermissionList.length != 0
      ) {
        //Get list of datalake Principals
        dataLakePrincipals = listDatasetPermissionResponseConsumer.dataPermissionList.map(
          (dataPermission) => dataPermission.dataLakePrincipal,
        );
      }
      //Add in principals from cart
      dataLakePrincipals = dataLakePrincipals.concat(
        props.cartItems
          .filter((dataPermission) => dataPermission.id === id)
          .map((dataPermission) => dataPermission.dataLakePrincipal),
      );
      dataLakeIAMPrincipals = dataLakePrincipals.filter(isValidPrincipalNotRoot);
      //Current workspace already consuming the all tables in database
      accountConsumed =
        dataLakePrincipals.filter((datalakePrincipal) => props.activeWorkspace.workspaceId.includes(datalakePrincipal))
          .length !== 0;
    } catch (err) {
      console.log('Exception list tag permission', err);
      return undefined;
    }
    return {
      permissionId: permissionId,
      accountConsumed: accountConsumed,
      publisherRole: publisherRole,
      iamPrincipals: dataLakeIAMPrincipals,
    };
  };

  const isGroupSelectedBelongToGalaxi = () => {
    let errorText = undefined;
    if (isDWaaSGroup(props.activeGroup)) {
      if (selectedPrincipalType == null || selectedPrincipalType.value != 'iamRoleUserAccess') {
        errorText = 'You must choose a Galaxi group to continue with your subscription';
      }
    }
    setGalaxiGroupErrorText(errorText);
    return errorText === undefined;
  };

  const getGalaxiGroupDropdown = () => {
    return (
      <FormField
        label='Target galaxi group'
        description='Select the target galaxi group for your subscription request'
        errorText={galaxiGroupErrorText}
      >
        <SpaceBetween size='m'>
          <Select
            selectedOption={selectedGalaxiGroup}
            onChange={(e) => {
              setGalaxiGroupErrorText(undefined);
              setSelectedGalaxiGroup(e.detail.selectedOption);
              setSelectedPrincipalType({
                label: 'IAM role/user access',
                value: 'iamRoleUserAccess',
              });
              setPrincipal(e.detail.selectedOption['iamRole']);
            }}
            options={dropdownOptions?.filter((dropdownOption) => dropdownOption.type == GALAXI_TYPE)}
            selectedAriaLabel='Selected'
            statusType={loadingResources ? 'loading' : 'finished'}
            loadingText='Loading access options'
          />
        </SpaceBetween>
      </FormField>
    );
  };

  const setupDropdownItems = async (tags, database) => {
    let dropdownItems = [];
    for (let tag of tags) {
      let tagId = `${tag.tagKey}.${tag.tagValue}.${tag.catalogId}`;
      let permissionData = await verifyRegisteredTag(
        tag.tagKey,
        tag.tagValue,
        tag.catalogId,
        database.Owners[0],
        tagId,
      );
      let tagPermissionId = permissionData.permissionId;
      let accountConsumed = permissionData.accountConsumed;
      if (tagPermissionId !== undefined) {
        dropdownItems.push({
          label: `Request ${tag.tagKey}.${tag.tagValue} access`,
          id: tagId,
          type: TAG_ITEM,
          value: tagId,
          tagKey: tag.tagKey,
          tagValue: tag.tagValue,
          catalogId: tag.catalogId,
          owner: database.Owners[0],
          catalogName: props.catalogName !== undefined ? props.catalogName : tag.catalogId,
          permissionId: tagPermissionId,
          workspaceAccess: accountConsumed,
          principals: permissionData.iamPrincipals,
        });
      }
    }
    let resourceId = `All Tables.${database?.DatabaseName}.${database?.CatalogId}`;
    let permissionData = await verifyAllTableAccess(database?.DatabaseName, database?.CatalogId, resourceId);
    if (permissionData !== undefined) {
      let databasePermissionId = permissionData.permissionId;
      let accountConsumed = permissionData.accountConsumed;
      let publisherRole = permissionData.publisherRole;
      if (databasePermissionId !== undefined) {
        if (!isMPData(database?.CatalogId, database?.DatabaseName, ALL_TABLES_RES_NAME)) {
          dropdownItems.push({
            label: `Request "All Tables" access from ` + `"${database?.DatabaseName}"`,
            id: resourceId,
            value: resourceId,
            type: RESOURCE_ITEM,
            datasetName: ALL_TABLES_RES_NAME,
            databaseName: database?.DatabaseName,
            owner: database?.Owners[0],
            catalogId: database?.CatalogId,
            bucketName: database?.BucketName,
            catalogName: props.catalogName !== undefined ? props.catalogName : database?.CatalogId,
            permissionId: databasePermissionId,
            publisherRole: publisherRole,
            workspaceAccess: accountConsumed,
            principals: permissionData.iamPrincipals,
          });
        }
      }
    }
    if (props.galaxiGroups != null && props.galaxiGroups?.length > 0) {
      for (let galaxiGroup of props.galaxiGroups) {
        dropdownItems.push({
          label: galaxiGroup?.groupName,
          value: galaxiGroup?.groupName,
          type: GALAXI_TYPE,
          teamName: galaxiGroup?.teamName,
          iamRole: galaxiGroup?.iamRole,
          groupEmail: galaxiGroup?.groupEmail,
        });
      }
    }
    setDropdownOptions(dropdownItems);
  };

  const handleRefresh = async () => {
    setLoadingResources(true);
    await setupDropdownItems(props.tags, props.database);
    setLoadingResources(false);
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  const handleConfirm = async () => {
    if (
      selectedPermission == undefined ||
      (isDWaaSGroup(props.activeGroup) && !isGroupSelectedBelongToGalaxi()) ||
      selectedPrincipalType == undefined ||
      (selectedPrincipalType.value === 'iamRoleUserAccess' && !validateIAMPrincipal())
    ) {
      return;
    }
    if (selectedPermission.type == RESOURCE_ITEM) {
      let ds: CartAccessItem = flattenDatasetAllTableAccess(
        selectedPermission.catalogId,
        selectedPermission.catalogName,
        selectedPermission.permissionId,
        selectedPermission.owner,
        selectedPermission.datasetName,
        selectedPermission.databaseName,
        selectedPermission.id,
        selectedPermission.publisherRole,
        principal,
      );
      props.addToCart([ds]);
    } else if (selectedPermission.type == TAG_ITEM) {
      let cartItem: CartAccessItem = await flattenTagAccess(
        selectedPermission.catalogId,
        selectedPermission.catalogName,
        selectedPermission.permissionId,
        selectedPermission.tagKey,
        selectedPermission.tagValue,
        selectedPermission.owner,
        selectedPermission.id,
        principal,
      );
      props.addToCart([cartItem]);
    }
    props.dismiss();
  };

  const transferResource = async (selectedPermission) => {
    setLoadingResources(true);
    let res = [];
    if (selectedPermission == undefined) {
      return res;
    }
    //named resource
    if (selectedPermission?.type == RESOURCE_ITEM) {
      res.push({
        type: DATASET_RESOURCE_TYPE,
        resource: `${selectedPermission.catalogName}/${selectedPermission.databaseName}/${selectedPermission.datasetName}`,
        resourceLink: createDatabaseDetailLink(
          selectedPermission.bucketName?.length > 0 ? S3_TABLE_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID,
          selectedPermission.catalogId,
          undefined,
          undefined,
          selectedPermission.bucketName,
          selectedPermission.databaseName,
          props.database['Region'],
        ),
      });
      setItems(res);
    } else if (selectedPermission.type == TAG_ITEM) {
      //tag resource
      let getTaggedResourcesResponse = await getTaggedResources({
        tagPair: selectedPermission.tagKey + ':' + selectedPermission.tagValue,
        catalogId: selectedPermission.catalogId,
        region: props.database['Region'],
        type: LAKEFORMATION_TAG_TYPE,
      });
      for (let resource of getTaggedResourcesResponse.resources) {
        if (resource.resourceType == DATASET_RESOURCE_TYPE) {
          res.push({
            type: DATASET_RESOURCE_TYPE,
            resource: `${selectedPermission.catalogName}/${resource.databaseName}/${resource.resourceName}`,
            resourceLink: createDatasetDetailLink(resource.resourceId),
          });
        } else if (resource.resourceType == DATABASE_RESOURCE_TYPE) {
          res.push({
            type: DATABASE_RESOURCE_TYPE,
            resource: `${selectedPermission.catalogName}/${resource.databaseName}`,
            resourceLink: createDatabaseDetailLink(
              selectedPermission.bucketName?.length > 0 ? S3_TABLE_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID,
              resource.catalogId,
              undefined,
              undefined,
              selectedPermission.bucketName,
              resource.databaseName,
              resource.region,
            ),
          });
        }
      }
      setItems(res);
    }
    setLoadingResources(false);
  };

  const { items } = useCollection(allItems, {
    filtering: {
      empty: <EmptyState title={'No resources'} subtitle={'No resources to display.'} />,
    },

    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  const validateIAMPrincipal = () => {
    let errorText = !isValidPrincipalNotRoot(principal)
      ? 'Invalid IAM principal ARN'
      : selectedPermission.principals.filter((dataLakePrincipal) => dataLakePrincipal === principal).length > 0
      ? 'Principal already consumed or is in your cart'
      : undefined;
    setPrincipalErrorText(errorText);
    return errorText === undefined;
  };

  const displayResource = () => {
    return (
      <Table
        columnDefinitions={[
          {
            id: 'resource',
            header: 'Resource',
            cell: (item) => <Link href={item.resourceLink}>{item.resource}</Link>,
          },
          {
            id: 'type',
            header: 'Resource type',
            cell: (item) => item.type,
          },
        ]}
        items={items}
        loading={loadingResources}
        loadingText='Loading resources'
        sortingDisabled
        header={<Header> Resources </Header>}
      />
    );
  };

  const handleChange = (e) => {
    setSelectedPermission(e.detail.selectedOption);
    setSelectedPrincipalType(undefined);
    setSelectedGalaxiGroup(undefined);
    setDropDownPrincipalOptions([
      {
        label: 'Workspace access (Entire account)',
        value: 'workspaceAccess',
        disabled: e.detail.selectedOption.workspaceAccess,
      },
      {
        label: 'IAM role/user access',
        value: 'iamRoleUserAccess',
      },
    ]);
    transferResource(e.detail.selectedOption);
  };

  return (
    <Modal
      onDismiss={props.dismiss}
      visible={props.visible}
      closeAriaLabel='Close modal'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={props.dismiss}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleConfirm}>
              Add to cart
            </Button>
          </SpaceBetween>
        </Box>
      }
      size='medium'
      header='Select data access approach'
    >
      <ColumnLayout>
        <FormField label='Select Lake Formation access permission type'>
          <Select
            selectedOption={selectedPermission}
            onChange={handleChange}
            options={dropdownOptions.filter((dropdownOption) => dropdownOption.type != GALAXI_TYPE)}
            selectedAriaLabel='Selected'
            empty={'All Tables access not supported for ' + `"${props.database.DatabaseName}"`}
            statusType={loadingResources ? 'loading' : 'finished'}
            loadingText='Loading access options'
          />
        </FormField>
        {selectedPermission !== undefined && !isDWaaSGroup(props.activeGroup) && (
          <FormField label='Select Lake Formation access principal type'>
            <Select
              selectedOption={selectedPrincipalType}
              onChange={(e) => {
                setSelectedPrincipalType(e.detail.selectedOption);
              }}
              options={dropdownPrincipalOptions}
              statusType={loadingResources ? 'loading' : 'finished'}
              loadingText='Loading access options'
            />
          </FormField>
        )}
        {selectedPermission !== undefined && isDWaaSGroup(props.activeGroup) && getGalaxiGroupDropdown()}
        {selectedPermission !== undefined &&
          selectedPrincipalType !== undefined &&
          selectedPrincipalType.value === 'iamRoleUserAccess' && (
            <FormField
              label='IAM User/Role'
              description='Enter the ARN for the IAM user or role'
              errorText={principalErrorText}
            >
              <Input
                ariaRequired={true}
                placeholder={'arn:aws:iam::123456789012:role/AWSDGSFoundry-ManagementRole-DO-NOT-DELETE'}
                value={principal}
                onChange={(e) => {
                  setPrincipalErrorText(undefined);
                  setPrincipal(e.detail.value);
                }}
                disabled={selectedGalaxiGroup != null}
              />
            </FormField>
          )}
        {selectedPermission !== undefined && displayResource()}
      </ColumnLayout>
    </Modal>
  );
};
